/**
 * What this client knows about **its own character**, from the two channels the
 * server describes one on.
 *
 * `./session` folds where every *body* is; this holds what the seat behind one
 * of them has — its two pools, its level, its experience and its point ledger.
 * They are deliberately not the same value. A position is interpolated,
 * predicted and read once a frame; a life total is none of those things, it is
 * simply the newest thing the server said, and threading it through a fold that
 * exists to reconcile prediction would put it on a timeline it does not belong
 * to.
 *
 * ## Two channels, because the server has two
 *
 * `PlayerState` carries the pools, the level and the lifetime experience,
 * because a party frame draws them and they are therefore broadcast. The point
 * ledger is not broadcast: it is `SERVER_MESSAGE.progression`, addressed to one
 * connection, *"paid once at join and once per change, to one person"*
 * (`@d2/schema`'s `messages.ts`). So the two arrive separately, at different
 * rates, and either can be the first to land — which is why `read()` answers
 * with two independently nullable halves rather than with one object that is
 * only ever whole.
 *
 * ## Why it is a store and not a `setState`
 *
 * A room patches its state twenty times a second. A life total moves about once
 * a second and a level once a run, so a component that set state per snapshot
 * would re-render the HUD twenty times a second to draw exactly the same orb.
 * `receiveVitals` compares before it replaces, and hands back the *same* object
 * when nothing moved — which is also precisely the contract
 * `useSyncExternalStore` requires of a snapshot getter, so React's own
 * bail-out does the rest.
 *
 * The ledger takes the opposite rule and for the opposite reason: it is only
 * ever sent when it changed, so there is nothing to save by comparing, and a
 * comparison that treated a re-allocation summing to the same totals as "no
 * change" would leave the tree drawn wrong until the next level-up.
 *
 * Nothing here imports `@colyseus/schema` or `@d2/schema`. Both shapes below are
 * declared structurally, exactly as `./prediction` declares `AuthoritativeState`
 * rather than taking a `PlayerState` (`TODO.md` #185): `./wire` is the one place
 * that reads the wire, and `@d2/schema`'s `ProgressionPayload` satisfies
 * `ProgressionLedger` without this module ever naming it.
 */

/**
 * What the wire says about the character behind a body.
 *
 * These six travel together because the server writes them together — the room's
 * `replicateCharacter` calls them *"the three things that are true of a
 * character whether or not it has a body: its life, its pool and what it has
 * earned"* — and because the HUD draws them together.
 *
 * A level of zero is not a level: it is `PlayerState`'s initial value, and it
 * means this room has not described this seat's progression. What to do about
 * that is the panel's caller's decision, not this module's.
 */
export interface CharacterVitals {
  readonly life: number;
  readonly maximumLife: number;
  readonly mana: number;
  readonly maximumMana: number;
  readonly level: number;
  /** Lifetime experience, as core's `EXPERIENCE_TABLE` counts it. Never progress inside a level. */
  readonly experience: number;
}

/** Points standing on one skill. The wire's own shape — a key at zero is an absent entry. */
export interface SkillAllocationView {
  readonly skillId: string;
  readonly points: number;
}

/**
 * The seat's own ledger, as `SERVER_MESSAGE.progression` reports it.
 *
 * Narrower than the payload on purpose: the two panels draw the two unspent
 * totals and the skill allocations, and nothing anywhere draws somebody's
 * attribute *allocation*. Declaring only what is consumed is what keeps this
 * module from having an opinion about a field it never reads.
 */
export interface ProgressionLedger {
  readonly skillPointsUnspent: number;
  readonly statPointsUnspent: number;
  readonly skills: readonly SkillAllocationView[];
}

/** Everything known about this character right now. Either half may still be absent. */
export interface CharacterFacts {
  /** The newest broadcast state for this seat, or `null` before the first snapshot. */
  readonly vitals: CharacterVitals | null;
  /** The newest ledger, or `null` before the server has reported one. */
  readonly ledger: ProgressionLedger | null;
}

export interface CharacterFeed {
  /** The facts as they stand. The **same object** until one of them changes. */
  readonly read: () => CharacterFacts;
  /** Watches for a change. Hands back the way to stop watching. */
  readonly subscribe: (listener: () => void) => () => void;
  /** One seat's broadcast state, off the wire (`./wire`). */
  readonly receiveVitals: (vitals: CharacterVitals) => void;
  /** One `SERVER_MESSAGE.progression`, off the wire. */
  readonly receiveProgression: (ledger: ProgressionLedger) => void;
}

const EMPTY: CharacterFacts = { vitals: null, ledger: null };

/** Whether two readings of a seat's state are the same reading. */
const sameVitals = (left: CharacterVitals | null, right: CharacterVitals): boolean =>
  left !== null &&
  left.life === right.life &&
  left.maximumLife === right.maximumLife &&
  left.mana === right.mana &&
  left.maximumMana === right.maximumMana &&
  left.level === right.level &&
  left.experience === right.experience;

export const createCharacterFeed = (): CharacterFeed => {
  let facts = EMPTY;
  const listeners = new Set<() => void>();

  const publish = (next: CharacterFacts): void => {
    facts = next;
    // Copied before iterating: a listener that unsubscribes itself while being
    // told would otherwise mutate the set mid-iteration.
    for (const listener of [...listeners]) listener();
  };

  return {
    read: (): CharacterFacts => facts,

    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener);

      return (): void => {
        listeners.delete(listener);
      };
    },

    receiveVitals: (vitals: CharacterVitals): void => {
      if (sameVitals(facts.vitals, vitals)) return;

      publish({ ...facts, vitals });
    },

    receiveProgression: (ledger: ProgressionLedger): void => {
      publish({ ...facts, ledger });
    },
  };
};
